import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { componentWithCondition } from '@atlaskit/platform-feature-flags-react';
import { fg } from '@atlaskit/platform-feature-flags';

import { type ForgeDoc, type RenderFn } from '@atlassian/forge-ui-types';

import { type ComponentMap, RendererNext } from '..';
import { makeAvatar, makeNativeUserPicker } from '../../components';
import { makeADFRenderer } from '../../components/UIKit/adfrenderer';
import { UserGroup } from '../../components/UIKit/usergroup';
import { HeightAnalyticsWrapper } from './HeightAnalyticsWrapper';
import { useUIKitComponents } from './useUIKitComponents';

import { WebRuntime } from '../../web-runtime';
import { type UIKitRendererProps } from './types';
import { uncurlyUuid } from './utils';

const UIKitRendererOld = (props: UIKitRendererProps): React.JSX.Element => {
	const {
		accountId,
		bridge,
		components,
		coreData,
		contextIds,
		contextChangeListeners,
		entryPoint,
		extension,
		extensionData,
		extensionViewData,
		getContextToken,
		mentionProvider,
		product,
		locale,
		modalExtension,
		onForgeDocUpdated,
		getThreeLOPrompt,
		customBridgeMethods,
		customBridge,
		customBridgeContext,
		loadingComponent,
		onError,
		extensionPayload,
		onSuccess,
		onAppError,
		viewportDimensions,
		isEmbeddedMacro,
		embeddedMacroParentType,
		embeddedParentLocalId,
		viewOnClose,
	} = props;

	const { cloudId, workspaceId } = coreData;

	const [forgeDoc, setForgeDoc] = useState<ForgeDoc | undefined>(undefined);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [shouldShowRenderer, setShouldShowRenderer] = useState(true);

	const isMacro = extension.type === 'xen:macro';

	const injectedComponentsWithKey = useMemo(() => {
		return components
			? Object.keys(components).reduce((acc, key) => {
					const Component = components[key];
					return {
						...acc,
						// A key is required to ensure React creates new component instances when forgeDoc.key changes.
						// Without this key, React reuses the same component instance across different forgeDoc updates,
						// causing hook call order violations when the component structure changes between renders.
						[key]: ((args) => <Component {...args} key={args.forgeDoc.key} />) as RenderFn,
					};
				}, {})
			: {};
	}, [components]);

	const wiredComponents = useMemo(
		() => ({
			Avatar: makeAvatar(),
			AvatarStack: UserGroup,
			UserPicker: makeNativeUserPicker({
				mentionProvider,
				accountId,
				...(workspaceId ? { cloudId: 'bitbucket' } : { cloudId }),
				productAttributes: workspaceId ? { workspaceIds: [uncurlyUuid(workspaceId)] } : undefined,
				productKey: product,
			}),
			...{ AdfRenderer: makeADFRenderer({ bridge }) },
		}),
		[accountId, cloudId, mentionProvider, product, workspaceId, bridge],
	);

	const getComponents = useCallback(
		(defaults: ComponentMap) => ({
			...defaults,
			...wiredComponents,
			...injectedComponentsWithKey,
		}),
		[injectedComponentsWithKey, wiredComponents],
	);

	const handleError = useCallback(
		(error: Error) => {
			if (onError) {
				onError(error.message);
			}

			if (onAppError) {
				onAppError(error.message);
			}
		},
		[onError, onAppError],
	);

	useEffect(() => {
		if (onError && error) {
			onError(error.message);
		}

		if (onAppError && error) {
			onAppError(error.message);
		}
	}, [error, onError, onAppError]);

	return (
		<HeightAnalyticsWrapper viewportDimensions={isMacro ? viewportDimensions : undefined}>
			{shouldShowRenderer && (
				<RendererNext
					forgeDoc={forgeDoc}
					extension={extension}
					error={error?.message}
					components={getComponents}
					modalExtension={
						modalExtension
							? {
									...modalExtension,
									viewOnClose,
								}
							: modalExtension
					}
					extensionData={extensionData}
					extensionPayload={extensionPayload}
					extensionType={extensionData.type}
					loading
					loadingComponent={loadingComponent}
					onError={handleError}
					onSuccess={onSuccess}
				/>
			)}
			<WebRuntime
				accountId={accountId}
				setForgeDoc={(forgeDoc: ForgeDoc) => {
					if (forgeDoc.type !== 'MacroConfig') {
						setForgeDoc(forgeDoc);
					}
					onForgeDocUpdated?.(forgeDoc);
				}}
				setError={setError}
				components={getThreeLOPrompt}
				contextIds={contextIds}
				extension={extension}
				coreData={coreData}
				extensionData={extensionData}
				extensionPayload={extensionPayload}
				extensionViewData={extensionViewData}
				locale={locale}
				bridge={bridge}
				getContextToken={getContextToken}
				entryPoint={entryPoint}
				customBridgeMethods={customBridgeMethods}
				customBridge={customBridge}
				customBridgeContext={customBridgeContext}
				setShouldShowRenderer={setShouldShowRenderer}
				isEmbeddedMacro={isEmbeddedMacro}
				embeddedMacroParentType={embeddedMacroParentType}
				embeddedParentLocalId={embeddedParentLocalId}
				contextChangeListeners={contextChangeListeners}
			/>
		</HeightAnalyticsWrapper>
	);
};

const UIKitRendererNew = (props: UIKitRendererProps): React.JSX.Element => {
	const {
		accountId,
		bridge,
		components,
		coreData,
		contextIds,
		contextChangeListeners,
		entryPoint,
		extension,
		extensionData,
		extensionViewData,
		getContextToken,
		mentionProvider,
		product,
		locale,
		modalExtension,
		onForgeDocUpdated,
		getThreeLOPrompt,
		customBridgeMethods,
		customBridge,
		customBridgeContext,
		loadingComponent,
		onError,
		extensionPayload,
		onSuccess,
		onAppError,
		viewportDimensions,
		isEmbeddedMacro,
		embeddedMacroParentType,
		embeddedParentLocalId,
		viewOnClose,
		isEmbeddedUIKitInCustomUI,
	} = props;

	const { cloudId, workspaceId } = coreData;

	const [forgeDoc, setForgeDoc] = useState<ForgeDoc | undefined>(undefined);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [shouldShowRenderer, setShouldShowRenderer] = useState(true);

	const isMacro = extension.type === 'xen:macro';

	const getComponents = useUIKitComponents({
		accountId,
		bridge,
		cloudId,
		workspaceId,
		mentionProvider,
		product,
		components,
	});

	const handleError = useCallback(
		(error: Error) => {
			if (onError) {
				onError(error.message);
			}

			if (onAppError) {
				onAppError(error.message);
			}
		},
		[onError, onAppError],
	);

	useEffect(() => {
		if (onError && error) {
			onError(error.message);
		}

		if (onAppError && error) {
			onAppError(error.message);
		}
	}, [error, onError, onAppError]);

	return (
		<HeightAnalyticsWrapper viewportDimensions={isMacro ? viewportDimensions : undefined}>
			{shouldShowRenderer && (
				<RendererNext
					forgeDoc={forgeDoc}
					extension={extension}
					error={error?.message}
					components={getComponents}
					modalExtension={
						modalExtension
							? {
									...modalExtension,
									viewOnClose,
								}
							: modalExtension
					}
					extensionData={extensionData}
					extensionPayload={extensionPayload}
					extensionType={extensionData.type}
					loading
					loadingComponent={loadingComponent}
					onError={handleError}
					onSuccess={onSuccess}
					isEmbeddedUIKitInCustomUI={isEmbeddedUIKitInCustomUI}
				/>
			)}
			<WebRuntime
				accountId={accountId}
				setForgeDoc={(forgeDoc: ForgeDoc) => {
					if (forgeDoc.type !== 'MacroConfig') {
						setForgeDoc(forgeDoc);
					}
					onForgeDocUpdated?.(forgeDoc);
				}}
				setError={setError}
				components={getThreeLOPrompt}
				contextIds={contextIds}
				extension={extension}
				coreData={coreData}
				extensionData={extensionData}
				extensionPayload={extensionPayload}
				extensionViewData={extensionViewData}
				locale={locale}
				bridge={bridge}
				getContextToken={getContextToken}
				entryPoint={entryPoint}
				customBridgeMethods={customBridgeMethods}
				customBridge={customBridge}
				customBridgeContext={customBridgeContext}
				setShouldShowRenderer={setShouldShowRenderer}
				isEmbeddedMacro={isEmbeddedMacro}
				embeddedMacroParentType={embeddedMacroParentType}
				embeddedParentLocalId={embeddedParentLocalId}
				contextChangeListeners={contextChangeListeners}
			/>
		</HeightAnalyticsWrapper>
	);
};

export const UIKitRenderer: React.FC<UIKitRendererProps> = componentWithCondition(
	() => fg('platform_forge_ui_uikit_modal_in_custom_ui_parent'),
	UIKitRendererNew,
	UIKitRendererOld,
);
